import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Icon,
  IconButton,
  Link,
  Popover,
  Text,
} from '@nimbus-ds/components';
import { DownloadIcon, EllipsisIcon } from '@nimbus-ds/icons';
import { useToast } from 'commons/hooks';
import { ReportStatus } from 'commons/types';
import { downloadFromUrl, useTranslationWithPrefix } from 'commons/utils';
import {
  useWithdrawalPdfReportGeneration,
  useWithdrawalReportGeneration,
} from '../../hooks';
import { useWithdrawalReportAlert } from './WithdrawalReportAlertContext';

interface WithdrawalReportButtonProps {
  withdrawalId: number;
  isApproved: boolean;
}

function WithdrawalReportButton({
  withdrawalId,
  isApproved,
}: WithdrawalReportButtonProps): JSX.Element {
  const { t } = useTranslationWithPrefix('withdraws.withdrawsTable');
  const { setLoadingAlert, setSuccessAlert, closeLoadingAlert } =
    useWithdrawalReportAlert();
  const [isOpen, setIsOpen] = React.useState(false);
  const { showToastError } = useToast();
  const handleError = useCallback(() => {
    closeLoadingAlert();
    showToastError(t('reportAlerts.error.description'));
  }, [closeLoadingAlert, showToastError, t]);

  const {
    startReportGeneration: startCsvGeneration,
    isLoading: isCsvLoading,
    report: csvReport,
  } = useWithdrawalReportGeneration({ onError: handleError });

  const {
    startReportGeneration: startPdfGeneration,
    isLoading: isPdfLoading,
    report: pdfReport,
  } = useWithdrawalPdfReportGeneration({ onError: handleError });

  const downloadedUrlsRef = useRef<Set<string>>(new Set());
  const downloadReportOnce = useCallback(
    (temporaryDownloadUrl: string) => {
      if (downloadedUrlsRef.current.has(temporaryDownloadUrl)) return;
      downloadedUrlsRef.current.add(temporaryDownloadUrl);
      downloadFromUrl(temporaryDownloadUrl);
      setSuccessAlert(temporaryDownloadUrl);
    },
    [setSuccessAlert],
  );

  useEffect(() => {
    if (
      csvReport?.status === ReportStatus.Completed &&
      csvReport.temporaryDownloadUrl
    ) {
      downloadReportOnce(csvReport.temporaryDownloadUrl);
    }
  }, [csvReport, downloadReportOnce]);

  useEffect(() => {
    if (
      pdfReport?.status === ReportStatus.Completed &&
      pdfReport.temporaryDownloadUrl
    ) {
      downloadReportOnce(pdfReport.temporaryDownloadUrl);
    }
  }, [pdfReport, downloadReportOnce]);

  const handleGenerateCsvReport = () => {
    if (!isApproved || isCsvLoading || isPdfLoading) return;
    setIsOpen(false);
    setLoadingAlert();
    startCsvGeneration(withdrawalId);
  };

  const handleGeneratePdfReport = () => {
    if (!isApproved || isCsvLoading || isPdfLoading) return;
    setIsOpen(false);
    setLoadingAlert();
    startPdfGeneration(withdrawalId);
  };

  const isDisabled = !isApproved;
  const isLoading = isCsvLoading || isPdfLoading;

  const menuContent = (
    <Box display="flex" flexDirection="column" gap="2">
      <Link
        as="button"
        onClick={handleGenerateCsvReport}
        textDecoration="none"
        appearance="neutral"
        disabled={isDisabled || isLoading}
      >
        <Box display="flex" gap="2" alignItems="center">
          <Icon source={<DownloadIcon />} color="neutral-textLow" />
          <Text color="neutral-textHigh">
            {isCsvLoading
              ? t('reportButton.generatingCSV')
              : t('reportButton.downloadCSV')}
          </Text>
        </Box>
      </Link>
      <Link
        as="button"
        onClick={handleGeneratePdfReport}
        textDecoration="none"
        appearance="neutral"
        disabled={isDisabled || isLoading}
      >
        <Box display="flex" gap="2" alignItems="center">
          <Icon source={<DownloadIcon />} color="neutral-textLow" />
          <Text color="neutral-textHigh">
            {isPdfLoading
              ? t('reportButton.generatingPDF')
              : t('reportButton.downloadPDF')}
          </Text>
        </Box>
      </Link>
    </Box>
  );

  return (
    <Popover
      content={menuContent}
      position="bottom-end"
      arrow={false}
      enabledClick={!isDisabled}
      offset={4}
      visible={isOpen}
      onVisibility={setIsOpen}
    >
      <IconButton
        source={<EllipsisIcon />}
        size="2rem"
        style={{ transform: 'rotate(90deg)' }}
        aria-label={t('reportButton.openMenu')}
        disabled={isDisabled}
      />
    </Popover>
  );
}

export default WithdrawalReportButton;
